import { useCallback, useEffect, useRef, useState } from "react";
import { useLang } from "../hooks/useLang";
import { useAnswerLang } from "../hooks/useAnswerLang";
import {
  listChatSessions,
  newChatSession,
  getChatSession,
  deleteChatSession,
  sendChatMessage,
  type ChatSession,
  type ChatMessage,
} from "../services/chat";
import Markdown from "./Markdown";
import Icon from "./icons";

type Props = {
  // Geçmiş paneli gizli mi (Mevzuat Analizi'ndeki örnek-soru paneliyle aynı toggle).
  sidebarHidden: boolean;
  onToggleSidebar: () => void;
};

// AI Sohbet — Mevzuat Analizi ile aynı iki-kolon düzen: solda app-grid "Geçmiş
// sohbetler" paneli (örnek sorular paneliyle aynı görünüm), sağda geniş sohbet
// alanı. Sohbet balonları + giriş kutusu aynı kalır.
export default function ChatPage({ sidebarHidden, onToggleSidebar }: Props) {
  const { t } = useLang();
  const { lang: answerLang } = useAnswerLang();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Yanıtı panoya kopyala (ham markdown metni) + kısa süre ✓ göster.
  function copyMsg(i: number, textToCopy: string) {
    navigator.clipboard?.writeText(textToCopy).then(() => {
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1500);
    }).catch(() => {});
  }

  const reloadSessions = useCallback(async () => {
    try { setSessions(await listChatSessions()); } catch { /* sessiz */ }
  }, []);

  useEffect(() => { void reloadSessions(); }, [reloadSessions]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  async function openSession(id: string) {
    setErr(null);
    try {
      const s = await getChatSession(id);
      setActiveId(s.id);
      setMessages(s.messages ?? []);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  async function startNew() {
    setErr(null);
    try {
      const s = await newChatSession();
      setActiveId(s.id);
      setMessages([]);
      await reloadSessions();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  async function onDelete(id: string) {
    if (!window.confirm(t("chat_delete_confirm"))) return;
    try {
      await deleteChatSession(id);
      if (activeId === id) { setActiveId(null); setMessages([]); }
      await reloadSessions();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  async function send() {
    const msg = input.trim();
    if (!msg || sending) return;
    setErr(null);
    let id = activeId;
    if (!id) {
      try { const s = await newChatSession(); id = s.id; setActiveId(s.id); }
      catch (e) { setErr(e instanceof Error ? e.message : String(e)); return; }
    }
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      const r = await sendChatMessage(id, msg, answerLang);
      setMessages(r.session.messages ?? []);
      await reloadSessions();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Geçmiş sohbetler — app-grid sol paneli (örnek sorular paneliyle aynı yer/görünüm) */}
      {!sidebarHidden && (
        <aside className="sidebar chat-history">
          <h2>{t("chat_history_label")} · {sessions.length}</h2>
          <button className="btn-pri chat-new-btn" onClick={startNew}>+ {t("chat_new")}</button>
          <div className="chat-hist-list">
            {sessions.map((s) => (
              <div
                key={s.id}
                className={"chat-session-item" + (s.id === activeId ? " active" : "")}
                onClick={() => openSession(s.id)}
                title={s.title || t("chat_untitled")}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.title || t("chat_untitled")}
                </span>
                <button onClick={(e) => { e.stopPropagation(); void onDelete(s.id); }} aria-label={t("da_remove")}>×</button>
              </div>
            ))}
          </div>
        </aside>
      )}

      <main className={"main chat-view" + (sidebarHidden ? " main-full" : "")}>
        <div className="main-inner">
          <div className="content-toolbar">
            <button
              className="sidebar-toggle"
              onClick={onToggleSidebar}
              title={sidebarHidden ? t("chat_hist_show") : t("chat_hist_hide")}
              aria-label="history toggle"
            >
              <Icon name={sidebarHidden ? "menu" : "x"} size={15} /> {sidebarHidden ? t("chat_hist_show") : t("chat_hist_hide")}
            </button>
          </div>

          <div className="chat-pane">
            <div ref={scrollRef} className="chat-scroll">
              {messages.length === 0 && !sending ? (
                <div className="empty" style={{ padding: "60px 20px" }}>
                  <Icon name="chat" size={38} />
                  <h3>{t("chat_page_title")}</h3>
                  <p>{t("chat_page_intro")}</p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={"chat-msg " + m.role}>
                    <div className="chat-bubble">
                      {m.role === "assistant" ? <Markdown text={m.content} /> : m.content}
                      {m.role === "assistant" && m.content && (
                        <button
                          className="chat-copy"
                          onClick={() => copyMsg(i, m.content)}
                          title={t("chat_copy")}
                          aria-label={t("chat_copy")}
                        >
                          {copiedIdx === i ? "✓" : "⧉"}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
              {sending && (
                <div className="chat-msg assistant">
                  <div className="chat-bubble"><span className="spinner" style={{ width: 14, height: 14 }} /> {t("chat_asking")}</div>
                </div>
              )}
            </div>

            {err && <div className="error">{err}</div>}

            <form
              onSubmit={(e) => { e.preventDefault(); void send(); }}
              className="chat-input-row"
            >
              <textarea
                className="search-input"
                style={{ flex: 1, minHeight: 46, border: "1px solid var(--border)", borderRadius: 10, background: "var(--bg-elev)" }}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat_input_placeholder")}
                rows={2}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
              />
              <button type="submit" className="btn-pri" disabled={sending || !input.trim()}>{t("chat_send")}</button>
            </form>
          </div>
        </div>
      </main>
    </>
  );
}
